// Cravy product catalog — used by /products and /products/:slug
// Each product appears on its own detail page.

export const PRODUCTS = [
  {
    slug: "starter-market",
    name: "Starter Market",
    tagline: "A self-serve micro-market for properties just getting started.",
    category: "Micro Market",
    image: "/assets/starter-market.jpg",
    short:
      "Our most popular entry point. A small checkout shelf paired with a glass-door cooler turns any nook into a curated 24/7 store.",
    description: [
      "The Starter Market is the perfect first step for properties that want an upscale convenience experience without committing the floor space of a full mini-mart.",
      "A single checkout kiosk handles the entire purchase flow — residents and guests grab what they want from the open shelf or cooler, scan, and go in under sixty seconds. No staff. No bottlenecks.",
      "We curate the mix specifically for your community — snacks, candy, single-serve meals, premium beverages, and grab-and-go favorites — and we restock 1–2 times a week.",
    ],
    features: [
      "Open snack shelf + tall glass-door cooler",
      "Self-checkout kiosk with card / tap / Apple Pay / Google Pay",
      "300+ SKU capacity across snacks, meals & drinks",
      "Single-day installation, plug-and-play",
      "Real-time inventory monitoring & curated restocking",
    ],
    specs: [
      { k: "Footprint",   v: "~5' wide × 30\" deep" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "300+ items" },
      { k: "Install",     v: "~3 days" },
    ],
    bestFor: ["Apartments (≤150 units)", "Small offices", "Boutique hotels", "Medical waiting rooms"],
    badge: "Most Popular",
  },
  {
    slug: "snack-beverage-combo",
    name: "Snack + Beverage Combo",
    tagline: "The classic. Two zones, one machine.",
    category: "Classic Vending",
    image: "/assets/snack-beverage-combo.jpg",
    short:
      "A single-footprint combo unit that vends both snacks and drinks. Perfect when space is tight but variety matters.",
    description: [
      "The combo machine is a workhorse — the format you've seen succeed in offices and apartment lobbies for decades, rebuilt with modern payment hardware and our live inventory monitoring.",
      "The top zone holds your curated snack lineup; the bottom drops bottles, cans, and protein drinks. One unit, one outlet, two amenities.",
      "Wrapped in a vibrant 'Eat Better · Feel Better' graphic that signals 'this isn't a generic vending machine.'",
    ],
    features: [
      "Snack zone + refrigerated beverage zone",
      "Card, tap, Apple Pay, Google Pay, cash",
      "Vibrant Eat Better · Feel Better wrap",
      "Single 115V outlet, compact footprint",
      "Up to 400 items across both zones",
    ],
    specs: [
      { k: "Footprint",   v: "36\"W × 36\"D × 72\"H" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "400+ items" },
      { k: "Install",     v: "1-2 days" },
    ],
    bestFor: ["Warehouses", "Schools", "Apartment laundry rooms", "Tight-footprint offices"],
  },
  {
    slug: "imbera-vr27-qr",
    name: "Imbera VR27 QR · A-POP",
    tagline: "AI-powered grab-and-go cooler. No buttons, no scanning.",
    category: "AI Cooler",
    image: "/assets/imbera-vr27.jpg",
    short:
      "Open the door, take what you want, close the door. The A-POP cooler uses computer vision to charge you for exactly what you grabbed.",
    description: [
      "Powered by intuitivo.ai, the VR27 QR is the closest thing to a frictionless Amazon-Go style experience in a single-cabinet form factor.",
      "Tap your card or phone to unlock, take any combination of items, and walk away. The internal cameras and AI detect what you removed and charge automatically — no buttons, no barcodes, no checkout line.",
      "Slim profile makes it ideal for spots where a full open-concept market won't fit but you still want a premium experience.",
    ],
    features: [
      "Computer vision auto-checkout",
      "Tap card / phone to unlock",
      "Single-cabinet, slim footprint",
      "7 shelves — sandwiches, snacks, drinks, meals",
      "Real-time inventory + sales analytics",
    ],
    specs: [
      { k: "Footprint",   v: "29\"W × 30\"D × 78\"H" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "~120 items across 7 shelves" },
      { k: "Tech",        v: "intuitivo.ai vision system" },
    ],
    bestFor: ["Premium apartment lobbies", "Corporate offices", "Hotel marketplaces", "Co-working spaces"],
    badge: "AI-Powered",
  },
  {
    slug: "us1200-ct-ultra",
    name: "US1200 CT Ultra",
    tagline: "24-hour smart vending, scaled up.",
    category: "Smart Cooler",
    image: "/assets/us1200-ct-ultra.jpg",
    short:
      "A double-door smart cooler with swipe-to-unlock, grab-and-go shopping, and auto-checkout in under sixty seconds.",
    description: [
      "The US1200 CT Ultra is built for high-traffic locations that need serious capacity. Two full glass doors, six shelves deep, and a smart-checkout system that finishes the transaction in 60 seconds.",
      "Swipe or tap to unlock → grab whatever you want → close the doors → auto-checkout charges you for what you took.",
      "Holds the full Cravy beverage lineup plus chilled snacks, sandwiches, and ready meals.",
    ],
    features: [
      "Dual-door smart cooler",
      "Swipe / Tap to unlock",
      "Auto-checkout in 60s",
      "Six-shelf, two-door capacity",
      "Ideal for 200+ daily transactions",
    ],
    specs: [
      { k: "Footprint",   v: "62\"W × 32\"D × 80\"H" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "500+ items" },
      { k: "Checkout",    v: "<60 seconds" },
    ],
    bestFor: ["Large apartment communities", "Corporate cafeterias", "Hotels", "Gyms"],
  },
  {
    slug: "taste-series",
    name: "Taste Series",
    tagline: "A clean, single-door cooler for premium beverages and snacks.",
    category: "Single-Door Cooler",
    image: "/assets/taste-series.jpg",
    short:
      "Compact glass-door cooler with a slim self-checkout — the cleanest format for tight corners and waiting-area corners.",
    description: [
      "The Taste Series keeps things minimal. A single tall glass door shows off the lineup; a small touchscreen on the side handles payment. That's it.",
      "Best for properties that want the upscale-cooler aesthetic without the visual weight of a full combo unit or open market.",
      "Mixes chilled drinks, single-serve snacks, candy, and grab-and-go items in one tidy footprint.",
    ],
    features: [
      "Single full-height glass door",
      "Side-mounted checkout touchscreen",
      "Premium snack + beverage mix",
      "Compact footprint for narrow spaces",
      "Card / tap / Apple Pay / Google Pay",
    ],
    specs: [
      { k: "Footprint",   v: "30\"W × 30\"D × 80\"H" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "200+ items" },
      { k: "Install",     v: "1-2 days" },
    ],
    bestFor: ["Medical offices", "Boutique gyms", "Small apartment lobbies", "Waiting rooms"],
  },
  {
    slug: "stockwell-2",
    name: "Stockwell 2.0",
    tagline: "Furniture-grade micro-market in a single cabinet.",
    category: "Open Concept",
    image: "/assets/stockwell.jpg",
    short:
      "Two open glass cabinets with a center checkout — designed to look like a piece of furniture, not a vending machine.",
    description: [
      "The Stockwell 2.0 is for properties that want the function of a micro-market without the look of a vending machine. Two open cabinets, a center touchscreen, and a finish that reads more 'specialty grocery' than 'snack bar.'",
      "Holds chilled drinks, dairy, meal kits, snacks, and convenience items in one polished footprint.",
      "Most often spec'd by design-forward apartment communities and creative offices.",
    ],
    features: [
      "Dual cabinet, open-concept design",
      "Furniture-grade finish",
      "Center self-checkout touchscreen",
      "Holds chilled + ambient products",
      "Looks like a piece of millwork, not a vending machine",
    ],
    specs: [
      { k: "Footprint",   v: "60\"W × 24\"D × 52\"H" },
      { k: "Power",       v: "115V standard 3-prong outlet" },
      { k: "Capacity",    v: "350+ items" },
      { k: "Finish",      v: "Espresso millwork / matte black" },
    ],
    bestFor: ["Design-forward apartments", "Creative offices", "Hotel marketplaces", "Wellness clubs"],
    badge: "Design-Forward",
  },
  {
    slug: "medium-market",
    name: "Medium Market",
    tagline: "Three-zone install for high-traffic properties.",
    category: "Full Install",
    image: "/assets/medium-market.jpg",
    short:
      "A full-wall micro-market: snack wall + checkout + cooler. The complete in-house convenience store, installed in a day.",
    description: [
      "The Medium Market is the upgrade from Starter Market. A dedicated checkout zone, a full snack wall with shelving and pegs, and a tall beverage cooler — all wall-mounted into a single, cohesive install.",
      "Capacity to support 200+ daily transactions, with a product mix wide enough to function as a true residents' or team's go-to.",
      "We handle the install (typically one day), stocking, restocking, and ongoing operations — you just enjoy the amenity.",
    ],
    features: [
      "Dedicated checkout + recharge zone",
      "Open snack wall with adjustable shelving",
      "Tall glass-door beverage cooler",
      "Wall-mounted, designed to integrate with the space",
      "Built for high-volume properties",
    ],
    specs: [
      { k: "Footprint",   v: "~12' wide × 30\" deep" },
      { k: "Power",       v: "115V standard outlet" },
      { k: "Capacity",    v: "600+ items" },
      { k: "Install",     v: "~3 days" },
    ],
    bestFor: ["Large apartment communities (150+ units)", "Large corporate offices", "Hotels", "University residences"],
    badge: "Highest Capacity",
  },
];

export const getProductBySlug = (slug) => PRODUCTS.find((p) => p.slug === slug);
